import { STORAGE_KEYS } from "@/shared/constants/storage-keys";

function getStorage(storage?: Storage): Storage | null {
  if (storage) return storage;
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function getSubject(token: string): string | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const subject = JSON.parse(atob(padded)) as { sub?: unknown };

    if (
      (typeof subject.sub === "string" && subject.sub) ||
      (typeof subject.sub === "number" && Number.isFinite(subject.sub))
    ) {
      return String(subject.sub);
    }

    return null;
  } catch {
    return null;
  }
}

function readPendingUsers(storage: Storage | null): string[] {
  if (!storage) return [];

  try {
    const value = storage.getItem(STORAGE_KEYS.onboardingPendingUsers);
    if (!value) return [];

    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed)
      ? parsed.flatMap((item) => {
          if (typeof item === "string") return [item];
          if (typeof item === "number" && Number.isFinite(item)) {
            return [String(item)];
          }
          return [];
        })
      : [];
  } catch {
    return [];
  }
}

function writePendingUsers(storage: Storage | null, users: string[]): void {
  if (!storage) return;

  try {
    if (users.length === 0) {
      storage.removeItem(STORAGE_KEYS.onboardingPendingUsers);
      return;
    }

    storage.setItem(STORAGE_KEYS.onboardingPendingUsers, JSON.stringify(users));
  } catch {
    // Onboarding must never block authentication when storage is unavailable.
  }
}

export function isOnboardingPending(token: string, storage?: Storage): boolean {
  const subject = getSubject(token);
  if (!subject) return false;

  return readPendingUsers(getStorage(storage)).includes(subject);
}

export function markOnboardingPending(token: string, storage?: Storage): void {
  const subject = getSubject(token);
  const targetStorage = getStorage(storage);
  if (!subject || !targetStorage) return;

  const users = readPendingUsers(targetStorage);
  if (!users.includes(subject)) {
    writePendingUsers(targetStorage, [...users, subject]);
  }
}

export function completeOnboarding(token: string, storage?: Storage): void {
  const subject = getSubject(token);
  const targetStorage = getStorage(storage);
  if (!subject || !targetStorage) return;

  writePendingUsers(
    targetStorage,
    readPendingUsers(targetStorage).filter((user) => user !== subject),
  );
}
